import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useGetEmployersOffboardingEmployees } from 'utils/web_api'

export type OffboardingStep = 'select' | 'details' | 'submit'

export interface EmployeeOffboardingData {
  employeeId: string
  finalDayOfEmployment: string | null
  cobraEligible: boolean | null
  personalEmail: string
}

export interface EmployeeInfo {
  id: string
  first_name: string
  last_name: string
  personal_email: string | null
  employment_status: string
  end_date: string | null // From payroll sync, used to pre-populate final day of employment
  start_date: string | null
  will_receive_offboarding_email: boolean
  is_hidden: boolean // hide_from_views - explicitly hidden from list
  is_muted: boolean // on leave until silence_pending_termination_until
  silence_pending_termination_until: string | null
}

interface BulkOffboardingState {
  currentStep: OffboardingStep
  currentEmployeeIndex: number
  selectedEmployeeIds: Set<string>
  employeeOffboardingData: Map<string, EmployeeOffboardingData>
  employees: EmployeeInfo[]
  totalEmployeeCount: number
}

interface BulkOffboardingContextValue extends BulkOffboardingState {
  setCurrentStep: (step: OffboardingStep) => void
  setCurrentEmployeeIndex: (index: number) => void
  toggleEmployeeSelection: (employeeId: string) => void
  selectAllOnPage: (employeeIds: string[]) => void
  deselectAllOnPage: (employeeIds: string[]) => void
  selectAllEmployees: (includeHidden?: boolean) => void
  clearAllSelections: () => void
  setEmployeeOffboardingData: (employeeId: string, data: Partial<EmployeeOffboardingData>) => void
  removeEmployee: (employeeId: string) => void
  setEmployees: (employees: EmployeeInfo[]) => void
  setTotalEmployeeCount: (count: number) => void
  getSelectedEmployees: () => EmployeeInfo[]
  goToNextEmployee: () => boolean
  goToPreviousEmployee: () => void
  isConnectedToPayroll: boolean
  isEditing: boolean
}

const BulkOffboardingContext = createContext<BulkOffboardingContextValue | null>(null)

interface BulkOffboardingProviderProps {
  children: React.ReactNode
  isConnectedToPayroll: boolean
  employeeId?: string | null
  initialStep?: OffboardingStep
  initialEmployees?: EmployeeInfo[]
  initialSelectedIds?: string[]
  initialOffboardingData?: Map<string, EmployeeOffboardingData>
}

export function BulkOffboardingProvider ({
  children,
  isConnectedToPayroll,
  employeeId,
  initialStep,
  initialEmployees,
  initialSelectedIds,
  initialOffboardingData,
}: BulkOffboardingProviderProps) {
  const isEditing = !!employeeId
  const [currentStep, setCurrentStep] = useState<OffboardingStep>(initialStep ?? (isEditing ? 'details' : 'select'))
  const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(0)
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<string>>(new Set(initialSelectedIds ?? []))
  const [employeeOffboardingData, setEmployeeOffboardingDataMap] = useState<Map<string, EmployeeOffboardingData>>(initialOffboardingData ?? new Map())
  const [employees, setEmployees] = useState<EmployeeInfo[]>(initialEmployees ?? [])
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(initialEmployees?.length ?? 0)

  // Fetch employee data for edit mode
  const { data: employeeData } = useGetEmployersOffboardingEmployees(
    { 'employee_ids[]': employeeId ? [employeeId] : [] },
    { query: { enabled: isEditing } }
  )

  // Fetch employee data for pre-selected employees (non-edit mode)
  const hasInitialSelectedIds = !isEditing && initialSelectedIds && initialSelectedIds.length > 0
  const { data: selectedEmployeesData } = useGetEmployersOffboardingEmployees(
    { 'employee_ids[]': initialSelectedIds ?? [] },
    { query: { enabled: !!hasInitialSelectedIds } }
  )

  // Initialize edit mode with employee data
  useEffect(() => {
    if (isEditing && employeeData && employeeData.length > 0) {
      const employee = employeeData[0]
      // Convert API employee to EmployeeInfo format
      const employeeInfo: EmployeeInfo = {
        id: employee.id,
        first_name: employee.first_name,
        last_name: employee.last_name,
        personal_email: employee.personal_email ?? null,
        employment_status: employee.employment_status,
        end_date: employee.end_date ?? null,
        start_date: employee.start_date ?? null,
        will_receive_offboarding_email: employee.will_receive_offboarding_email,
        is_hidden: employee.is_hidden ?? false,
        is_muted: employee.is_muted ?? false,
        silence_pending_termination_until: employee.silence_pending_termination_until ?? null,
      }

      setEmployees([employeeInfo])
      setSelectedEmployeeIds(new Set([employee.id]))
      setTotalEmployeeCount(1)

      // Pre-populate offboarding data from existing termination_config if it exists
      const today = new Date().toISOString().split('T')[0]
      setEmployeeOffboardingDataMap(new Map([[
        employee.id,
        {
          employeeId: employee.id,
          finalDayOfEmployment: employee.effective_date ?? employee.end_date ?? today,
          cobraEligible: employee.cobra_eligible ?? null,
          personalEmail: employee.personal_email ?? '',
        },
      ]]))
    }
  }, [isEditing, employeeData])

  // Initialize pre-selected employees (non-edit mode)
  useEffect(() => {
    if (hasInitialSelectedIds && selectedEmployeesData && selectedEmployeesData.length > 0) {
      const employeeInfos: EmployeeInfo[] = selectedEmployeesData.map(emp => ({
        id: emp.id,
        first_name: emp.first_name,
        last_name: emp.last_name,
        personal_email: emp.personal_email ?? null,
        employment_status: emp.employment_status,
        end_date: emp.end_date ?? null,
        start_date: emp.start_date ?? null,
        will_receive_offboarding_email: emp.will_receive_offboarding_email,
        is_hidden: emp.is_hidden ?? false,
        is_muted: emp.is_muted ?? false,
        silence_pending_termination_until: emp.silence_pending_termination_until ?? null,
      }))

      setEmployees(employeeInfos)
      setTotalEmployeeCount(employeeInfos.length)

      // Initialize offboarding data for each employee
      const today = new Date().toISOString().split('T')[0]
      const dataMap = new Map<string, EmployeeOffboardingData>()
      employeeInfos.forEach(emp => {
        dataMap.set(emp.id, {
          employeeId: emp.id,
          finalDayOfEmployment: emp.end_date ?? today,
          cobraEligible: null,
          personalEmail: emp.personal_email ?? '',
        })
      })
      setEmployeeOffboardingDataMap(dataMap)
    }
  }, [hasInitialSelectedIds, selectedEmployeesData])

  const toggleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId)
      } else {
        newSet.add(employeeId)
      }
      return newSet
    })
  }, [])

  const selectAllOnPage = useCallback((employeeIds: string[]) => {
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev)
      employeeIds.forEach(id => newSet.add(id))
      return newSet
    })
  }, [])

  const deselectAllOnPage = useCallback((employeeIds: string[]) => {
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev)
      employeeIds.forEach(id => newSet.delete(id))
      return newSet
    })
  }, [])

  const selectAllEmployees = useCallback((includeHidden = false) => {
    // Add all employee IDs to the selection.
    // When includeHidden is true (e.g., filtering by "on_leave"), include explicitly hidden employees.
    const employeesToSelect = includeHidden
      ? employees
      : employees.filter(e => !e.is_hidden)
    setSelectedEmployeeIds(new Set(employeesToSelect.map(e => e.id)))
  }, [employees])

  const clearAllSelections = useCallback(() => {
    setSelectedEmployeeIds(new Set())
  }, [])

  const setEmployeeOffboardingData = useCallback((employeeId: string, data: Partial<EmployeeOffboardingData>) => {
    setEmployeeOffboardingDataMap(prev => {
      const newMap = new Map(prev)
      const existing = newMap.get(employeeId) ?? {
        employeeId,
        finalDayOfEmployment: null,
        cobraEligible: null,
        personalEmail: '',
      }
      newMap.set(employeeId, { ...existing, ...data })
      return newMap
    })
  }, [])

  const removeEmployee = useCallback((employeeId: string) => {
    setSelectedEmployeeIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(employeeId)
      return newSet
    })
  }, [])

  const getSelectedEmployees = useCallback(() => {
    return employees.filter(e => selectedEmployeeIds.has(e.id))
  }, [employees, selectedEmployeeIds])

  const goToNextEmployee = useCallback(() => {
    const selectedEmployees = getSelectedEmployees()
    if (currentEmployeeIndex < selectedEmployees.length - 1) {
      setCurrentEmployeeIndex(prev => prev + 1)
      return true
    }
    // Move to submit step
    setCurrentStep('submit')
    return false
  }, [currentEmployeeIndex, getSelectedEmployees])

  const goToPreviousEmployee = useCallback(() => {
    if (currentEmployeeIndex > 0) {
      setCurrentEmployeeIndex(prev => prev - 1)
    } else {
      setCurrentStep('select')
    }
  }, [currentEmployeeIndex])

  const value = useMemo<BulkOffboardingContextValue>(() => ({
    currentStep,
    currentEmployeeIndex,
    selectedEmployeeIds,
    employeeOffboardingData,
    employees,
    totalEmployeeCount,
    setCurrentStep,
    setCurrentEmployeeIndex,
    toggleEmployeeSelection,
    selectAllOnPage,
    deselectAllOnPage,
    selectAllEmployees,
    clearAllSelections,
    setEmployeeOffboardingData,
    removeEmployee,
    setEmployees,
    setTotalEmployeeCount,
    getSelectedEmployees,
    goToNextEmployee,
    goToPreviousEmployee,
    isConnectedToPayroll,
    isEditing,
  }), [
    currentStep,
    currentEmployeeIndex,
    selectedEmployeeIds,
    employeeOffboardingData,
    employees,
    totalEmployeeCount,
    toggleEmployeeSelection,
    selectAllOnPage,
    deselectAllOnPage,
    selectAllEmployees,
    clearAllSelections,
    setEmployeeOffboardingData,
    removeEmployee,
    getSelectedEmployees,
    goToNextEmployee,
    goToPreviousEmployee,
    isConnectedToPayroll,
    isEditing,
  ])

  return (
    <BulkOffboardingContext.Provider value={value}>
      {children}
    </BulkOffboardingContext.Provider>
  )
}

export function useBulkOffboarding () {
  const context = useContext(BulkOffboardingContext)
  if (!context) {
    throw new Error('useBulkOffboarding must be used within a BulkOffboardingProvider')
  }
  return context
}
